
export interface TamraliptaReceiptData {
  receiptNumber: string;
  consumerName: string;
  connectionNumber: string;
  holdingNumber: string;
  wardNumber: string;
  months: string[];
  amount: number;          
  amountInWords: string;
  collectedDate: string;
  category?: string;
  feruleSize?: string;
  arrearAmount?: number;   
  monthlyAmount?: number;  
  phoneNumber?: string;    
  advanceAmountPaid?: number;
}

export function numToWords(num: number): string {
  if (num === 0) return "Zero Rupees Only";
  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  function c(n: number): string {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + c(n % 100) : "");
    if (n < 100000) return c(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + c(n % 1000) : "");
    if (n < 10000000) return c(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + c(n % 100000) : "");
    return c(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + c(n % 10000000) : "");
  }
  return c(Math.floor(num)) + " Rupees Only";
}

function drawLine(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y: number,
  x2: number
): void {
  ctx.save();
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(x1, y);
  ctx.lineTo(x2, y);
  ctx.stroke();
  ctx.restore();
}

// ── Dashed horizontal line ──
function drawDashedLine(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y: number,
  x2: number
): void {
  ctx.save();
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 0.8;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(x1, y);
  ctx.lineTo(x2, y);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

export function generateTamraliptaReceipt(data: TamraliptaReceiptData): void {
  const arrearAmt   = data.arrearAmount  ?? 0;
  const monthlyAmt  = data.monthlyAmount ?? 0;
  const advanceAmt  = data.advanceAmountPaid ?? 0;
  const hasArrear   = arrearAmt  > 0;
  const hasMonthly  = monthlyAmt > 0;
  const hasAdvance  = advanceAmt > 0;
  const hasPhone    = !!(data.phoneNumber && String(data.phoneNumber).trim());

  const baseH = hasPhone ? 920 : 874;
  const W = 1240;
  const rowCount = (hasArrear ? 1 : 0) + (hasMonthly ? 1 : 0) + (hasAdvance ? 1 : 0);
  const H = rowCount > 0 ? baseH + 60 + (rowCount * 46) : baseH;

  const canvas = document.createElement("canvas");
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;

  // ── White background ──
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  // ── Outer border ──
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 3.5;
  ctx.strokeRect(14, 14, W - 28, H - 28);

  // ── Inner border ──
  ctx.lineWidth = 1.2;
  ctx.strokeRect(22, 22, W - 44, H - 44);

  // ── Layout constants ──
  const PL = 48;
  const PT = 46;
  const PR = W - 48;
  const LH = 50;

  const fItalic = "italic 23px 'Times New Roman', Times, serif";
  const fBold   = "bold 23px 'Times New Roman', Times, serif";
  const fSmall  = "italic 20px 'Times New Roman', Times, serif";
  const fSmallB = "bold 20px 'Times New Roman', Times, serif";

  // ── Form No. 42 ──
  ctx.fillStyle = "#000000";
  ctx.textAlign = "left";
  ctx.font = "bold 21px 'Times New Roman', Times, serif";
  ctx.fillText("Form No. 42", PL, PT + 18);
  ctx.font = "18px 'Times New Roman', Times, serif";
  ctx.fillText("(Vide Rules 98 & 111)", PL, PT + 40);

  // ── Receipt Number ──
  ctx.textAlign = "right";
  ctx.font = "bold 19px 'Times New Roman', Times, serif";
  const receiptLabel = "Receipt No. ";
  const receiptNum   = data.receiptNumber;
  const fullText     = receiptLabel + receiptNum;
  const fullW        = ctx.measureText(fullText).width;
  ctx.fillText(fullText, PR, PT + 18);
  drawLine(ctx, PR - fullW, PT + 24, PR);

  // ── "Miscellaneous Receipt" banner ──
  const bannerY = PT + 74;
  const bannerW = 490;
  const bannerX = W / 2 - bannerW / 2;
  ctx.fillStyle = "#000000";
  ctx.fillRect(bannerX, bannerY, bannerW, 38);
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 23px 'Times New Roman', Times, serif";
  ctx.textAlign = "center";
  ctx.fillText("Miscellaneous Receipt", W / 2, bannerY + 27);

  // ── Tamralipta Municipality ──
  const municipalityName = localStorage.getItem("municipalityName")
  ctx.fillStyle = "#000000";
  ctx.font = "bold 62px 'Times New Roman', Times, serif";
  ctx.textAlign = "center";
  ctx.fillText(municipalityName ?? "Tamralipta Municipality", W / 2, bannerY + 100);

  // ── "Water Supply Department" banner ──
  const wsdY = bannerY + 114;
  const wsdW = 450;
  ctx.fillStyle = "#000000";
  ctx.fillRect(W / 2 - wsdW / 2, wsdY, wsdW, 36);
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 23px 'Times New Roman', Times, serif";
  ctx.textAlign = "center";
  ctx.fillText("Water Supply Department", W / 2, wsdY + 26);

  // ── Body ──
  ctx.fillStyle = "#000000";
  ctx.textAlign = "left";
  let curY = wsdY + 70;

  // ── Line 1: Received from (Consumer Name) ──
  ctx.font = fItalic;
  ctx.fillText("Received from", PL, curY);
  const rfl = ctx.measureText("Received from").width;
  ctx.font = fBold;
  ctx.fillText(data.consumerName, PL + rfl + 10, curY);
  drawLine(ctx, PL + rfl + 8, curY + 5, PR);

  // ── Line 1b: Phone Number (right-aligned, below name line) ──
  if (hasPhone) {
    curY += LH - 12;
    ctx.font = fSmall;
    ctx.textAlign = "left";
    ctx.fillStyle = "#000000";
    ctx.fillText("Phone No.", PL, curY);
    const phLabelW = ctx.measureText("Phone No.").width;
    ctx.font = fSmallB;
    ctx.fillText(String(data.phoneNumber).trim(), PL + phLabelW + 10, curY);
    drawLine(ctx, PL + phLabelW + 8, curY + 5, PL + phLabelW + 320);
  }

  // ── Line 2: Holding / Plot No. ──
  curY += LH;
  ctx.font = fItalic;
  const accountLine = "on account of excess water consumption in Water connection line in Holding / Plot No. ";
  ctx.fillText(accountLine, PL, curY);
  const accountLineW = ctx.measureText(accountLine).width;
  ctx.font = fBold;
  ctx.fillText(data.holdingNumber || "", PL + accountLineW, curY);
  drawLine(ctx, PL + accountLineW - 2, curY + 5, PR);

  // ── Line 3: Ward No. / Connection No. / for ──
  curY += LH;
  ctx.font = fItalic;
  ctx.fillText("Ward No.", PL, curY);
  const wnL = ctx.measureText("Ward No.").width;
  ctx.font = fBold;
  ctx.fillText(data.wardNumber || "", PL + wnL + 8, curY);
  drawLine(ctx, PL + wnL + 6, curY + 5, PL + wnL + 136);

  const connX = PL + wnL + 152;
  ctx.font = fItalic;
  ctx.fillText("Connection No.", connX, curY);
  const cnL = ctx.measureText("Connection No.").width;
  ctx.font = fBold;
  ctx.fillText(data.connectionNumber, connX + cnL + 8, curY);
  drawLine(ctx, connX + cnL + 6, curY + 5, PR - 66);
  ctx.font = fItalic;
  ctx.fillText("for", PR - 54, curY);

  // ── Line 4: the Month of ──
  curY += LH;
  ctx.font = fItalic;
  ctx.fillText("the  Month of", PL, curY);
  const tmL = ctx.measureText("the  Month of").width;
  const monthStr = data.months.length > 0 ? data.months.join(", ") : "—";
  ctx.font = fBold;
  ctx.fillText(monthStr, PL + tmL + 10, curY);
  drawLine(ctx, PL + tmL + 8, curY + 5, PR);

  // ── Blank underline ──
  curY += LH - 6;
  drawLine(ctx, PL, curY + 5, PR);

  // ════════════════════════════════════════════════════════
  //  PAYMENT BREAKDOWN SECTION
  // ════════════════════════════════════════════════════════
  if (hasArrear || hasMonthly || hasAdvance) {
    curY += LH + 4;

    drawDashedLine(ctx, PL, curY - 10, PR);

    ctx.font = "bold italic 19px 'Times New Roman', Times, serif";
    ctx.fillStyle = "#000000";
    ctx.textAlign = "center";
    ctx.fillText("— Payment Breakdown —", W / 2, curY + 10);
    ctx.textAlign = "left";
    curY += 36;

    const dotsEndX = PR - 200;

    const drawRow = (label: string, amount: number, yPos: number): void => {
      ctx.font = fSmall;
      ctx.fillStyle = "#000000";
      ctx.textAlign = "left";
      ctx.fillText(label, PL + 10, yPos);
      const labelW = ctx.measureText(label).width;
      ctx.font = "18px 'Times New Roman', Times, serif";
      let dx = PL + 10 + labelW + 8;
      while (dx < dotsEndX) { ctx.fillText(".", dx, yPos); dx += 7; }
      ctx.font = fSmallB;
      ctx.textAlign = "right";
      ctx.fillText(`Rs. ${amount.toLocaleString()}/-`, PR, yPos);
      ctx.textAlign = "left";
    };

    const activeRows: { label: string; amount: number }[] = [];
    if (hasMonthly) activeRows.push({ label: "Monthly Charges", amount: monthlyAmt });
    if (hasArrear) activeRows.push({ label: "Arrear Collected", amount: arrearAmt });
    if (hasAdvance) activeRows.push({ label: "Advance Payment Made", amount: advanceAmt });

    activeRows.forEach((row) => {
      drawRow(row.label, row.amount, curY);
      curY += LH - 8;
    });

    if (activeRows.length > 1) {
      curY += 12;
      drawLine(ctx, PR - 320, curY, PR);
      curY += 28;
      ctx.font = "bold 22px 'Times New Roman', Times, serif";
      ctx.fillStyle = "#000000";
      ctx.textAlign = "left";
      ctx.fillText("Total Amount Paid", PL + 10, curY);
      ctx.font = "bold 24px 'Times New Roman', Times, serif";
      ctx.textAlign = "right";
      ctx.fillText(`Rs. ${data.amount.toLocaleString()}/-`, PR, curY);
      ctx.textAlign = "left";
    }

    curY += 20;
    drawDashedLine(ctx, PL, curY, PR);
    curY += 14;
  }

  // ── Rs. line (total) ──
  curY += LH + 4;
  ctx.font = fItalic;
  ctx.fillText("Rs.", PL, curY);
  const rsL = ctx.measureText("Rs.").width;
  const rsVal = `${data.amount}/-`;
  ctx.font = fBold;
  ctx.fillText(rsVal, PL + rsL + 8, curY);
  drawLine(ctx, PL + rsL + 6, curY + 5, PL + rsL + 168);

  const rwX = PL + rsL + 182;
  ctx.font = fItalic;
  ctx.fillText("Rupees ( in words)", rwX, curY);
  const rwL = ctx.measureText("Rupees ( in words)").width;

  const words = data.amountInWords || numToWords(data.amount);
  ctx.font = fBold;
  const maxWW = PR - (rwX + rwL + 12);
  let wLine1 = "";
  let wLine2 = "";
  let curr   = "";
  for (const w of words.split(" ")) {
    const test = curr ? curr + " " + w : w;
    if (ctx.measureText(test).width > maxWW && curr) {
      if (!wLine1) { wLine1 = curr; curr = w; }
      else { wLine2 = curr; curr = w; }
    } else {
      curr = test;
    }
  }
  if (!wLine1) wLine1 = curr;
  else wLine2 = (wLine2 ? wLine2 + " " : "") + curr;

  ctx.fillText(wLine1, rwX + rwL + 10, curY);
  drawLine(ctx, rwX + rwL + 8, curY + 5, PR);
  if (wLine2) {
    curY += LH - 10;
    ctx.fillText(wLine2, rwX + rwL + 10, curY);
    drawLine(ctx, rwX + rwL + 8, curY + 5, PR);
  }

  // ── Second blank underline ──
  curY += LH - 6;
  drawLine(ctx, PL, curY + 5, PR);

  // ── Bottom signatures ──
  const botY = H - 68;
  drawLine(ctx, PL, botY - 18, PR);

  ctx.fillStyle = "#000000";
  ctx.textAlign = "left";

  ctx.font = fItalic;
  ctx.fillText("Date", PL, botY);
  ctx.font = fBold;
  ctx.fillText(data.collectedDate, PL + 62, botY);
  drawLine(ctx, PL + 56, botY + 5, PL + 290);

  const mid = W / 2;
  ctx.font = fItalic;
  ctx.textAlign = "center";
  ctx.fillText("Cashier", mid, botY);
  drawLine(ctx, mid - 124, botY + 5, mid + 124);

  ctx.fillText("Vice-Chairman", PR - 116, botY);
  drawLine(ctx, PR - 262, botY + 5, PR);

  ctx.textAlign = "left";

  // ══════════════════════════
  //   Canvas → raw PDF binary
  // ══════════════════════════
  const imgData  = canvas.toDataURL("image/jpeg", 1.0);
  const base64   = imgData.split(",")[1];
  const imgBytes = atob(base64);
  const imgLen   = imgBytes.length;

  const pdfW = 841.89;
  const pdfH = 595.28;

  const enc = new TextEncoder();
  const hdr = "%PDF-1.4\n%\xFF\xFF\xFF\xFF\n";

  const o1 = "1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n";
  const o2 = "2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n";
  const o3 = `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pdfW.toFixed(2)} ${pdfH.toFixed(2)}] /Contents 4 0 R /Resources << /XObject << /Im1 5 0 R >> >> >>\nendobj\n`;
  const sc  = `q ${pdfW.toFixed(2)} 0 0 ${pdfH.toFixed(2)} 0 0 cm /Im1 Do Q`;
  const o4  = `4 0 obj\n<< /Length ${sc.length} >>\nstream\n${sc}\nendstream\nendobj\n`;
  const o5h = `5 0 obj\n<< /Type /XObject /Subtype /Image /Width ${W} /Height ${H} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imgLen} >>\nstream\n`;
  const o5f = `\nendstream\nendobj\n`;

  const p1 = hdr.length;
  const p2 = p1 + o1.length;
  const p3 = p2 + o2.length;
  const p4 = p3 + o3.length;
  const p5 = p4 + o4.length;
  const xp = p5 + o5h.length + imgLen + o5f.length;

  const xref = [
    "xref", "0 6",
    "0000000000 65535 f ",
    String(p1).padStart(10, "0") + " 00000 n ",
    String(p2).padStart(10, "0") + " 00000 n ",
    String(p3).padStart(10, "0") + " 00000 n ",
    String(p4).padStart(10, "0") + " 00000 n ",
    String(p5).padStart(10, "0") + " 00000 n ",
    "", "trailer",
    "<< /Size 6 /Root 1 0 R >>",
    "startxref", String(xp), "%%EOF",
  ].join("\n");

  const part1 = enc.encode(hdr + o1 + o2 + o3 + o4 + o5h);
  const imgArr = new Uint8Array(imgLen);
  for (let i = 0; i < imgLen; i++) imgArr[i] = imgBytes.charCodeAt(i);
  const part2 = enc.encode(o5f + xref);

  const pdf = new Uint8Array(part1.length + imgLen + part2.length);
  pdf.set(part1, 0);
  pdf.set(imgArr, part1.length);
  pdf.set(part2, part1.length + imgLen);

  const blob = new Blob([pdf], { type: "application/pdf" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `Receipt_${data.receiptNumber}_${data.connectionNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 8000);
}